import React, { useEffect, useRef, useState } from 'react';
import { Route, Routes } from 'react-router-dom';
import { useBeforeMount } from '../Helpers/CustomHooks';
import { defaultGlobalContext, GlobalContext } from '../context/GlobalContext';
import { getDefaultBreadcrumbs, getLocaleInfo } from './Functions';
import { setIntlForFormatMessage } from '../Localization/formatMessage';
import { createIntl, IntlProvider } from 'react-intl';
import { localStorageInstance } from '../Storage/LocalStorage';
import { storageKeys } from '../config/variables';
import { ServiceClient } from '../Services/ServiceClient';
import { HttpErrorHandler, LoggingServiceErrorsHandler } from '../Services/ServiceErrorHandler';
import ErrorPage from '../Pages/ErrorPage';
import Auth from './Auth';
import Callback from '../Pages/Callback';
import CustomHeader from './CustomHeader';

const App = props => {
  const updateGlobalContext = context => {
    setGlobalContext({ ...globalContext, ...context });
  };

  const [localeInfo] = useState(getLocaleInfo());

  useBeforeMount(() => {
    setIntlForFormatMessage(createIntl(localeInfo));
  });

  const [globalContext, setGlobalContext] = useState(defaultGlobalContext);
  const [httpErrorCode, setHttpErrorCode] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState(getDefaultBreadcrumbs());
  const [isTestEnvironment, setIsTestEnvironment] = useState(false);

  const errorHandlers = useRef([]);

  useEffect(() => {
    var legalUnit = localStorageInstance.getItem(storageKeys?.legalUnit) ?? 'SE';
    var businessArea = localStorageInstance.getItem(storageKeys?.businessArea) ?? 'Private';

    updateGlobalContext({
      legalUnit: legalUnit,
      businessArea: businessArea,
    });

    errorHandlers.current.push(
      ServiceClient.registerGlobalErrorHandler(
        new HttpErrorHandler((errorMessage, errorData, resolutionContext) => {
          setHttpErrorCode(errorData.argument ?? 500);
        })
      )
    );
    errorHandlers.current.push(ServiceClient.registerGlobalErrorHandler(new LoggingServiceErrorsHandler()));
    const handlers = errorHandlers.current;

    ServiceClient.isTestEnvironment(result => {
      if (result.isTestEnvironment) setIsTestEnvironment(true);
    });

    // this will run only when this component unmounts
    return () => {
      handlers.forEach(x => ServiceClient.unregisterGlobalErrorHandler(x));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addBreadcrumbs = breadcrumbs => {
    setBreadcrumbs([...getDefaultBreadcrumbs(), ...breadcrumbs]);
  };

  const resetBreadcrumb = () => {
    setBreadcrumbs(getDefaultBreadcrumbs());
  };

  return (
    <IntlProvider locale={localeInfo.locale} messages={localeInfo.messages}>
      <GlobalContext.Provider
        value={{
          ...globalContext,
          addBreadcrumbs,
          resetBreadcrumb,
          breadcrumbs,
          isTestEnvironment,
        }}>
        <CustomHeader updateGlobalContext={updateGlobalContext} />
        <main>
          {httpErrorCode ? (
            <ErrorPage code={httpErrorCode} setError={x => setHttpErrorCode(x)} />
          ) : (
            <>
              <Routes>
                <Route path="/*" element={<Auth />} />
                <Route path="/callback" element={<Callback />} />
                <Route path="/error" exact element={<ErrorPage />} />
              </Routes>
            </>
          )}
        </main>
      </GlobalContext.Provider>
    </IntlProvider>
  );
};

export default App;
